import lodash from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IPaginationMeta } from "../../domain/pagination/pagination-meta";
import { IUserTableRow } from "../../domain/user/user-table-row";
import { IUsersFilter } from "../../repositories/queries/user/get-users.query";
import { useUsersContext } from "./users-context";

interface IUserTableProps {
  children: React.ReactNode;
}

interface IUserTableContext {
  users: IUserTableRow[];
  isLoading: boolean;
  entryCount: number;
  tablePagination: IPaginationMeta;
  setTablePagination: (pagination: IPaginationMeta) => void;
  filters?: IUsersFilter;
  setFilters: (filters: IUsersFilter) => void;
  isFilterDirty: boolean;
  resetFilter: () => void;
}

export const UserTableContext = createContext<IUserTableContext>({} as IUserTableContext);

export const useUserTableContext = () => {
  return useContext(UserTableContext);
};

const useUserTableProvider = (_props: IUserTableProps): IUserTableContext => {
  const { users, filters, isLoading, setFilters, tablePagination, setTablePagination } =
    useUsersContext();
  const [entryCount, setEntryCount] = useState<number>(50);

  useEffect(() => {
    setEntryCount(users && users.length > 50 ? 50 : users.length);
  }, [users]);

  const isFilterDirty = useMemo(
    () => !lodash.isEmpty(filters?.active) || !lodash.isEmpty(filters?.emailQuery) || !lodash.isEmpty(filters?.lastNameQuery),
    [filters],
  );

  const resetFilter = () => setFilters({ active: undefined, emailQuery: undefined, lastNameQuery: undefined, groupTypes: undefined });

  return {
    entryCount,
    filters,
    setFilters,
    tablePagination,
    setTablePagination,
    isLoading,
    users,
    isFilterDirty,
    resetFilter,
  };
};

export const UserTableProvider = (props: IUserTableProps) => {
  const value = useUserTableProvider(props);
  return <UserTableContext.Provider value={value}>{props.children}</UserTableContext.Provider>;
};
